import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/utils/api';

export function useAnalyzeEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emailId) => {
      const response = await api.post(`/secretary/analyze/${emailId}`);
      return response.data.data;
    },
    onSuccess: (_, emailId) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['emails', emailId] });
      queryClient.invalidateQueries({ queryKey: ['annotations', emailId] });
      queryClient.invalidateQueries({ queryKey: ['events', 'email', emailId] });
    }
  });
}

export function usePendingReplies() {
  return useQuery({
    queryKey: ['secretary', 'pending-replies'],
    queryFn: async () => {
      const response = await api.get('/secretary/pending-replies');
      return response.data.data ?? null;
    }
  });
}

export function useLatestSecretaryReport() {
  return useQuery({
    queryKey: ['secretary', 'latest-report'],
    queryFn: async () => {
      const response = await api.get('/secretary/reports/latest');
      return response.data;
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}

export function usePreviewDigest() {
  return useMutation({
    mutationFn: async (options) => {
      const response = await api.post('/secretary/digest/preview', options);
      return response.data;
    }
  });
}

export function useGenerateDigest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options) => {
      const response = await api.post('/secretary/digest', options);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate reports list and latest report when new digest is generated
      queryClient.invalidateQueries({ queryKey: ['secretary', 'reports'] });
      queryClient.invalidateQueries({ queryKey: ['secretary', 'latest-report'] });
      // Brief generation creates pending action suggestions — invalidate so Actions view picks them up
      queryClient.invalidateQueries({ queryKey: ['actions', 'suggestions'] });
    }
  });
}

export function useSecretaryReports({ limit = 50, offset = 0 } = {}) {
  return useQuery({
    queryKey: ['secretary', 'reports', { limit, offset }],
    queryFn: async () => {
      const params = new URLSearchParams({ limit, offset });
      const response = await api.get(`/secretary/reports?${params}`);
      return response.data;  // API interceptor already unwraps to {success, data}
    }
  });
}

export function useSecretaryReport(id) {
  return useQuery({
    queryKey: ['secretary', 'reports', id],
    queryFn: async () => {
      const response = await api.get(`/secretary/reports/${id}`);
      return response.data;  // API interceptor already unwraps to {success, data}
    },
    enabled: !!id
  });
}

export function useDeleteSecretaryReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/secretary/reports/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secretary', 'reports'] });
    }
  });
}

export function useMarkReportAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/secretary/reports/${id}/read`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secretary', 'reports'] });
      queryClient.invalidateQueries({ queryKey: ['secretary', 'latest-report'] });
    }
  });
}
